import { CommonModule, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, Output, EventEmitter, ViewEncapsulation, inject, signal, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToastMessageService } from '@k12/app-services';
import { ToastMessageType } from '@k12/models';
import { Notifications } from '@k12/models';
import { DateTimeformat } from '@k12/pipes';
import { DateTimeformatTooltip } from '@k12/pipes';
import { errorToastValue } from '@k12/app-services';
import { NotificationService } from '@k12/app-services';
import { Subject, takeUntil } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    DateTimeformat,
    DateTimeformatTooltip,
    MatBadgeModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule
  ],
  providers: [DatePipe],
  encapsulation: ViewEncapsulation.None,

  selector: 'lib-notification',
  styleUrl: './notification.component.scss',
  templateUrl: './notification.component.html'
})
export class LibNotificationComponent implements OnInit, OnDestroy {
  @Input() adminUrl: string;

  @Output() handleHidePanel = new EventEmitter();
  @Output() updateNotification = new EventEmitter();

  public notifications = signal<Notifications[]>([]);
  public notificationsCount = signal<number>(0);
  public showMore: boolean = false;

  private destroy$ = new Subject<void>();
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private toastMessageService = inject(ToastMessageService);

  public ngOnInit(): void {
    // Internal Subject; cannot error by construction, so no error handler is needed.
    this.notificationService.notification$
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) =>
        this.notifications.set(response.sort((a, b) => new Date(b.sentDate).getTime() - new Date(a.sentDate).getTime()))
      );

    // Internal BehaviorSubject; cannot error by construction, so no error handler is needed.
    this.notificationService.notificationCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => this.notificationsCount.set(response));
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public markAsRead(notification: Notifications): void {
    this.updateNotifications(notification.id, new Date().toISOString());
  }

  public markAsUnread(notification: Notifications): void {
    this.updateNotifications(notification.id, null);
  }

  public navigateLink(event: Event | MouseEvent): void {
    const aTag = (event.target as HTMLElement)?.closest('a');
    if (!aTag) return;
    const href = aTag.getAttribute('href');
    if (!href) return;
    event.preventDefault();
    this.router.navigateByUrl(href);
  }

  public hidePanel(): void {
    this.handleHidePanel.emit();
  }

  private updateNotifications(notificationId: string, readDate: string | null): void {
    this.notificationService.updateNotifications(this.adminUrl, notificationId, readDate).subscribe({
      error: (err: HttpErrorResponse) => {
        this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(err));
      }
    });
  }
}
